import { ReactNode, useContext, useEffect, useId, useMemo, useState } from "react";
import { Provider } from "react-redux";
import { skipToken } from "@reduxjs/toolkit/query";
import { List, ListItemText, Skeleton, Typography, useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";
import Lottie from "lottie-react";
import { PortalControllerContext } from "../../context";
import createStore from "./store";
import {
	useEndLiveSessionMutation,
	useGetAssessmentNameSuffixQuery,
	useGetAssessmentQuery,
	useGetFilteredLiveSessions,
	useGetLiveSessionActiveStateQuery,
	useGetLiveSessionCountQuery,
} from "./store/hooks";
import { ALL_SESSION_TYPES, LiveSession, LiveSessionActiveState, SessionType } from "./store/slices/liveSessions";
import i18n from "../../../i18n";
import { ThemeWrapper } from "@verity/components";
import {
	CoreButton,
	CoreExpandButton,
	CoreMenuButton,
	CoreMenuItem,
	CoreNoResults,
	CoreSearchField,
	CoreTabNumberIcon,
	CoreTabs,
	coreToast,
} from "@verity/components/core";
import { openAuthenticatedTab } from "../../../helpers/open-authenticated";
import { uuidToIntId } from "../../../helpers/uuid";
import { formatDuration } from "../../../helpers/time";
import { removeAssessmentNameSuffix } from "../../../helpers/assessment-name";
import TextSkeleton from "../../../utility/TextSkeleton";
import PageCard from "../page-card";
import VisualizerBanner from "../visualizer-banner";
import "react-toastify/dist/ReactToastify.css";
import styles from "./index.module.css";
import LiveSchedule from "../../../images/portal/live-schedule.svg?react";
import UsersCircle from "../../../images/portal/users-circle.svg?react";
import ProctorsCircle from "../../../images/portal/proctors-circle.svg?react";
import jumpingDotsAnimation from "../../../images/jumping-dots.json";
import greenTimerAnimation from "../../../images/portal/greenTimer.json";
import redTimerAnimation from "../../../images/portal/redTimer.json";

function SessionStateCard({ icon, label, children }: { icon: ReactNode; label: ReactNode; children: ReactNode }) {
	return (
		<div className={styles.sessionStateCard}>
			<div className={styles.stateCardIconWrapper}>{icon}</div>
			{label}
			{children}
		</div>
	);
}

function TimeRemainingCard({ session, currTimestamp }: { session: LiveSession; currTimestamp: number }) {
	const { endTime } = session;

	const duration = Math.max(new Date(endTime).getTime() - currTimestamp, 0);
	const almostOver = duration < 60 * 1000;

	return (
		<SessionStateCard
			icon={
				<Lottie
					className={styles.timerAnimation}
					animationData={almostOver ? redTimerAnimation : greenTimerAnimation}
				/>
			}
			label={i18n.t("portal.components.liveProctoring.indexTSX.timeRemaining")}
		>
			<div className={`${styles.timeDisplay} ${almostOver ? styles.almostOver : ""}`}>
				{formatDuration(duration, { allowDays: true })}
			</div>
		</SessionStateCard>
	);
}

function useGetUserSessionCount(liveSessionId: string, role: "proctor" | "student") {
	const { data: activeStateResult } = useGetLiveSessionActiveStateQuery(liveSessionId, {
		pollingInterval: 5000,
	});
	const activeState: LiveSessionActiveState = activeStateResult ?? { userSessions: [] };
	return {
		count: activeState.userSessions.filter((session) => session.role === role).length,
		isLoading: !activeStateResult,
	};
}

function DetailsLoadingAnimation() {
	return <Lottie className={styles.loadingAnimation} animationData={jumpingDotsAnimation} />;
}

function ActiveStudentsCard({ sessionId }: { sessionId: string }) {
	const { count, isLoading } = useGetUserSessionCount(sessionId, "student");
	return (
		<SessionStateCard icon={<UsersCircle />} label={i18n.t("portal.components.liveProctoring.indexTSX.activeStudents")}>
			<div className={styles.numberStateValue}>
				{isLoading ? <DetailsLoadingAnimation /> : String(count).padStart(2, "0")}
			</div>
		</SessionStateCard>
	);
}

function ActiveProctorsCard({ sessionId }: { sessionId: string }) {
	const { count, isLoading } = useGetUserSessionCount(sessionId, "proctor");

	return (
		<SessionStateCard
			icon={<ProctorsCircle />}
			label={i18n.t("portal.components.liveProctoring.indexTSX.activeProctors")}
		>
			<div className={styles.numberStateValue}>
				{isLoading ? <DetailsLoadingAnimation /> : String(count).padStart(2, "0")}
			</div>
		</SessionStateCard>
	);
}

function JoinButton({ session }: { session: LiveSession }) {
	const { id, users } = session;
	const { userInfo } = useContext(PortalControllerContext);

	return (
		<CoreButton
			className={styles.joinButton}
			disabled={!users.some(({ userId }) => userId === userInfo?.uuid)}
			disabledTooltip={i18n.t("portal.components.liveProctoring.indexTSX.cannotJoin")}
			onClick={async () => {
				const toastId = coreToast.loading(i18n.t("portal.components.liveProctoring.indexTSX.joining"));
				try {
					openAuthenticatedTab(`/live/meet.html?id=${id}&userId=${userInfo?.uuid}`)?.focus();
					toast.dismiss(toastId);
				} catch (error) {
					console.error(`Failed to join live proctored session. `, error);
					coreToast.update(toastId, i18n.t("portal.components.liveProctoring.indexTSX.joinFailError"), "error");
				}
			}}
		>
			{i18n.t("portal.components.liveProctoring.indexTSX.joinSession")}
		</CoreButton>
	);
}

function SessionMenuButton({ session }: { session: LiveSession }) {
	const { id } = session;

	const [endSession] = useEndLiveSessionMutation();

	const [menuShowing, setMenuShowing] = useState(false);

	return (
		<CoreMenuButton
			menuShowing={menuShowing}
			setMenuShowing={setMenuShowing}
			tooltip={i18n.t("portal.components.liveProctoring.indexTSX.actions")}
		>
			<CoreMenuItem onClick={() => endSession(id)}>
				<ListItemText primary={i18n.t(`portal.components.liveProctoring.indexTSX.endSession`)} />
			</CoreMenuItem>
		</CoreMenuButton>
	);
}

function SessionEntry({ session, currTimestamp }: { session: LiveSession; currTimestamp: number }) {
	const { id, name, startTime, type } = session;
	const startDate = new Date(startTime);

	const [expanded, setExpanded] = useState(false);

	const compactView = useMediaQuery("(max-width: 620px)", {
		matchMedia: (query) => {
			return window.matchMedia(query);
		},
	});

	return (
		<li className={`${styles.sessionEntry} ${styles[type]}`}>
			<div className={styles.sessionHeader}>
				<div className={styles.sessionHeaderWrappableArea}>
					<div className={styles.sessionInfoArea}>
						<Typography variant="h4" className={styles.sessionName}>
							{name}
						</Typography>
						{startDate.toLocaleDateString([], {
							month: "short",
							day: "numeric",
							year: "numeric",
						})}{" "}
						- {startDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
					</div>
					{type === "active" && (
						<div className={styles.sessionActions}>
							<JoinButton session={session} />
							{!compactView && (
								<CoreExpandButton
									expanded={expanded}
									onChange={setExpanded}
									expandedTooltip={i18n.t("portal.components.liveProctoring.indexTSX.collapse")}
									collapsedTooltip={i18n.t("portal.components.liveProctoring.indexTSX.expand")}
								/>
							)}
							<SessionMenuButton session={session} />
						</div>
					)}
				</div>
				{type === "active" && compactView && (
					<CoreExpandButton
						variant="plain"
						collapsedRight
						expanded={expanded}
						onChange={setExpanded}
						expandedTooltip={i18n.t("portal.components.liveProctoring.indexTSX.collapse")}
						collapsedTooltip={i18n.t("portal.components.liveProctoring.indexTSX.expand")}
					/>
				)}
			</div>
			{expanded && (
				<div className={styles.sessionStateDetails}>
					<TimeRemainingCard session={session} currTimestamp={currTimestamp} />
					<ActiveStudentsCard sessionId={id} />
					<ActiveProctorsCard sessionId={id} />
				</div>
			)}
		</li>
	);
}

function SessionEntrySkeleton() {
	return <Skeleton variant="rounded" className={styles.sessionEntrySkeleton} />;
}

function AssessmentSection({
	assessmentUuid,
	sessions,
	currTimestamp,
}: {
	assessmentUuid: string;
	sessions: LiveSession[];
	currTimestamp: number;
}) {
	const { data: nameSuffix } = useGetAssessmentNameSuffixQuery(undefined, {
		refetchOnMountOrArgChange: false,
		refetchOnFocus: false,
	});
	const { data: assessment, isLoading: assessmentLoading } = useGetAssessmentQuery(uuidToIntId(assessmentUuid));

	return (
		<li className={styles.assessmentSection}>
			<Typography variant="h3" className={styles.assessmentHeader}>
				{assessmentLoading ? <TextSkeleton /> : removeAssessmentNameSuffix(assessment?.name ?? "?", nameSuffix)}
			</Typography>
			<List className={styles.assessmentSessions}>
				{sessions.map((session) => (
					<SessionEntry key={session.id} session={session} currTimestamp={currTimestamp} />
				))}
			</List>
		</li>
	);
}

function AssessmentSectionSkeleton() {
	return (
		<div className={styles.assessmentSection}>
			<Typography variant="h3" className={styles.assessmentHeader}>
				<TextSkeleton />
			</Typography>
			<SessionEntrySkeleton />
			<SessionEntrySkeleton />
		</div>
	);
}

function SessionsTabContent({
	type,
	description,
	tabPanelAriaLabelledby,
}: {
	type: SessionType;
	description: ReactNode;
	tabPanelAriaLabelledby: string | undefined;
}) {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteUuid, courseUuid } = portalContext;

	const [searchFilter, setSearchFilter] = useState("");
	const {
		data: sessions,
		isLoading,
		isError,
		totalCount,
		refetch,
	} = useGetFilteredLiveSessions(
		instituteUuid && courseUuid
			? {
					instituteUuid,
					courseUuid,
					type,
				}
			: skipToken,
		{ searchText: searchFilter },
		{ pollingInterval: 30000 },
	);

	const [currTimestamp, setCurrTimestamp] = useState(Date.now());
	useEffect(() => {
		const interval = setInterval(() => setCurrTimestamp(Date.now()), 1000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		if (
			sessions?.some(({ startTime, endTime }) => {
				const startTimestamp = new Date(startTime).getTime();
				const endTimestamp = new Date(endTime).getTime();

				const isEnded = currTimestamp >= endTimestamp;
				const isActive = currTimestamp >= startTimestamp - 15 * 60 * 1000 && !isEnded;
				return (type === "active" && !isActive) || (type === "upcoming" && isActive);
			})
		) {
			refetch();
		}
	}, [type, currTimestamp]);

	const sessionsByAssessment: Record<string, LiveSession[]> = {};
	sessions?.forEach((session) => {
		if (!sessionsByAssessment[session.assessmentId]) {
			sessionsByAssessment[session.assessmentId] = [];
		}
		sessionsByAssessment[session.assessmentId].push(session);
	});

	const isLoaded = !isLoading && !isError;
	const noResultsKey = totalCount === 0 ? (`noSessions.${type}` as const) : "noResults";
	const controlId = useId();

	return (
		<div className={styles.sessionsContent} role="tabpanel" aria-labelledby={tabPanelAriaLabelledby}>
			<Typography className={styles.description}>{description}</Typography>
			{isLoaded && totalCount > 0 && (
				<CoreSearchField
					onSearch={setSearchFilter}
					label={i18n.t(`portal.components.liveProctoring.indexTSX.searchBy`)}
					ariaControls={controlId}
					totalResults={sessions?.length ?? 0}
					value={searchFilter}
				/>
			)}
			{isError && (
				<CoreNoResults
					variant="failed"
					header={i18n.t("portal.components.liveProctoring.indexTSX.loadingFailed.title")}
				>
					{i18n.t("portal.components.liveProctoring.indexTSX.loadingFailed.description")}
				</CoreNoResults>
			)}
			{isLoading && (
				<>
					<Skeleton variant="rounded" height={35} />
					<AssessmentSectionSkeleton />
					<AssessmentSectionSkeleton />
				</>
			)}
			{isLoaded && sessions && (
				<div id={controlId} style={{ display: "contents" }}>
					{sessions.length === 0 && (
						<CoreNoResults header={i18n.t(`portal.components.liveProctoring.indexTSX.${noResultsKey}.title`)}>
							{i18n.t(`portal.components.liveProctoring.indexTSX.${noResultsKey}.description`)}
						</CoreNoResults>
					)}
					{sessions.length > 0 && (
						<List className={styles.assessments}>
							{Object.entries(sessionsByAssessment).map(([assessmentUuid, assessmentSessions]) => (
								<AssessmentSection
									key={assessmentUuid}
									assessmentUuid={assessmentUuid}
									sessions={assessmentSessions}
									currTimestamp={currTimestamp}
								/>
							))}
						</List>
					)}
				</div>
			)}
		</div>
	);
}

function useTabSessionCount(type: SessionType) {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteUuid, courseUuid } = portalContext;
	return useGetLiveSessionCountQuery(
		instituteUuid && courseUuid
			? {
					instituteUuid,
					courseUuid,
					type,
				}
			: skipToken,
		{ pollingInterval: 30000 },
	);
}

function TabSessionCountIcon({ type }: { type: SessionType }) {
	const { data: count, isLoading } = useTabSessionCount(type);
	return <CoreTabNumberIcon count={count} isLoading={isLoading} />;
}

function TabBar({
	value,
	onChange,
	ids,
}: {
	value: SessionType;
	onChange: (tab: SessionType) => void;
	ids: Map<SessionType, string>;
}) {
	return (
		<CoreTabs<SessionType>
			secondary
			scrollable
			value={value}
			onChange={(_, tab) => onChange(tab)}
			tabs={ALL_SESSION_TYPES.map((type) => ({
				key: type,
				icon: <TabSessionCountIcon type={type} />,
				label: i18n.t(`portal.components.liveProctoring.indexTSX.tab.${type}`),
				id: ids.get(type),
			}))}
			width="fit-content"
		/>
	);
}

export default function LiveProctoringPage() {
	const store = useMemo(() => createStore(), []);
	const [tab, setTab] = useState<SessionType>("active");
	const tabIdSuffix = useId();
	const tabIds = useMemo(() => {
		return new Map(ALL_SESSION_TYPES.map((type) => [type, `${type}-tab-${tabIdSuffix}`]));
	}, [tabIdSuffix]);

	return (
		<Provider store={store}>
			<ThemeWrapper scopedCssBaselineProps={{ className: styles.page }}>
				<div className={styles.pageContainer}>
					<VisualizerBanner />
					<PageCard
						icon={<LiveSchedule />}
						title={i18n.t("portal.components.liveProctoring.indexTSX.liveAssessments")}
						style={{ minHeight: "min-content" }}
						titleVariant="h1"
					>
						<div className={styles.cardContent}>
							<TabBar value={tab} onChange={setTab} ids={tabIds} />
							<SessionsTabContent
								key={tab}
								type={tab}
								description={i18n.t(`portal.components.liveProctoring.indexTSX.tabDescription.${tab}`)}
								tabPanelAriaLabelledby={tabIds.get(tab)}
							/>
						</div>
					</PageCard>
				</div>
			</ThemeWrapper>
		</Provider>
	);
}
